/*
  SOAH mechanism proof - chapter switcher.

  Progressive enhancement only. The section is fully readable before this file
  runs: all five chapters are server-rendered and visible, and the rail is a set
  of ordinary in-page anchors. Nothing here is required for the content to be
  reachable by a crawler, a screen reader or a browser with JavaScript off.

  What it adds:
    - ARIA tablist semantics over the existing anchors
    - roving tabindex, arrow / Home / End keyboard navigation
    - automatic activation (panels are pre-rendered, so switching is free)
    - active-tab auto-centring in the mobile rail
    - scroll anchoring back to the masthead so a switch never lands mid-chapter
    - stable, deep-linkable chapter hashes via replaceState
    - the soah:chapter_viewed customer event

  Interaction only. Every listener is event-driven and user-initiated: there is
  no scroll listener, no resize listener, no IntersectionObserver, no
  ResizeObserver, no timer and no requestAnimationFrame anywhere in this file.
  All motion (the active underline and the panel crossfade) is CSS.

  Deliberately NOT included: horizontal swipe between panels. It conflicts with
  vertical scrolling, image drags, accordion taps and browser back-gestures, and
  the rail plus the Read next control are more discoverable.

  The disclosure analytics listener stays inline in the section and is untouched.
*/
(function () {
  'use strict';

  var SECTION_NAME = 'mechanism_proof';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function publish(eventName, payload) {
    if (!window.Shopify || !Shopify.analytics || typeof Shopify.analytics.publish !== 'function') { return; }
    try {
      var result = Shopify.analytics.publish(eventName, payload);
      if (result && typeof result.catch === 'function') { result.catch(function () {}); }
    } catch (e) { /* never block the UI on analytics */ }
  }

  function init(root) {
    if (!root || root.dataset.chBound === 'true') { return; }

    var track = root.querySelector('[data-ch-track]');
    var masthead = root.querySelector('[data-ch-masthead]');
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-ch-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-ch-panel]'));

    if (!track || tabs.length === 0 || tabs.length !== panels.length) { return; }

    root.dataset.chBound = 'true';
    root.classList.add('soah-ch--js');

    var pdpTemplate = root.getAttribute('data-pdp-template') || 'unknown';
    var current = 0;

    track.setAttribute('role', 'tablist');
    track.setAttribute('aria-orientation', 'horizontal');

    tabs.forEach(function (tab, i) {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panels[i].id);
      tab.setAttribute('tabindex', '-1');
      tab.setAttribute('aria-selected', 'false');
    });

    panels.forEach(function (panel) {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('tabindex', '0');
      panel.hidden = true;
    });

    function centreTab(smooth) {
      var tab = tabs[current];
      if (!tab || track.scrollWidth <= track.clientWidth) { return; }
      var target = tab.offsetLeft - (track.clientWidth - tab.offsetWidth) / 2;
      var max = track.scrollWidth - track.clientWidth;
      target = Math.max(0, Math.min(target, max));
      if (smooth && !reduceMotion && typeof track.scrollTo === 'function') {
        track.scrollTo({ left: target, behavior: 'smooth' });
      } else {
        track.scrollLeft = target;
      }
    }

    // A switch must never land the reader halfway down the new chapter. If the
    // masthead has scrolled above the viewport, bring it back into view.
    function anchorToMasthead() {
      if (!masthead) { return; }
      var top = masthead.getBoundingClientRect().top;
      if (top >= 0) { return; }
      if (typeof masthead.scrollIntoView === 'function') {
        try {
          masthead.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
          return;
        } catch (e) { /* older Safari: fall through */ }
      }
      masthead.scrollIntoView(true);
    }

    function select(index, opts) {
      var options = opts || {};
      if (index < 0 || index >= tabs.length) { return; }

      var previous = current;
      current = index;

      tabs.forEach(function (tab, i) {
        var on = i === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
      });

      panels.forEach(function (panel, i) {
        panel.classList.remove('is-entering');
        panel.hidden = i !== index;
      });

      var panel = panels[index];
      // The panel has just come back from display:none, which restarts CSS
      // animations on its own. No forced reflow, no JS-driven animation.
      if (!reduceMotion && previous !== index) { panel.classList.add('is-entering'); }

      centreTab(options.smooth !== false);

      if (options.focusTab) { tabs[index].focus(); }
      if (options.anchor) { anchorToMasthead(); }

      if (options.updateHash && window.history && typeof history.replaceState === 'function') {
        try { history.replaceState(null, '', '#' + panel.id); } catch (e) { /* ignore */ }
      }

      if (options.report && previous !== index) {
        publish('soah:chapter_viewed', {
          section_name: SECTION_NAME,
          chapter_index: index + 1,
          chapter_name: (tabs[index].textContent || '').replace(/\s+/g, ' ').trim(),
          pdp_template: pdpTemplate
        });
      }
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function (event) {
        event.preventDefault();
        select(i, { updateHash: true, report: true, anchor: true });
      });
    });

    // Automatic activation: arrow keys move focus and switch in one step, which
    // is the ARIA-recommended pattern when panels are already in the DOM.
    track.addEventListener('keydown', function (event) {
      var next = null;
      switch (event.key) {
        case 'ArrowRight': next = (current + 1) % tabs.length; break;
        case 'ArrowLeft': next = (current - 1 + tabs.length) % tabs.length; break;
        case 'Home': next = 0; break;
        case 'End': next = tabs.length - 1; break;
        default: return;
      }
      event.preventDefault();
      select(next, { focusTab: true, updateHash: true, report: true });
    });

    root.querySelectorAll('[data-ch-next]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        var target = parseInt(link.getAttribute('data-ch-next'), 10);
        if (isNaN(target)) { return; }
        select(target - 1, { updateHash: true, report: true, anchor: true });
      });
    });

    function indexFromHash() {
      var hash = window.location.hash;
      if (!hash || hash.length < 2) { return -1; }
      for (var i = 0; i < panels.length; i++) {
        if ('#' + panels[i].id === hash) { return i; }
      }
      return -1;
    }

    window.addEventListener('hashchange', function () {
      var i = indexFromHash();
      if (i > -1) { select(i, { report: true }); }
    });

    var initial = indexFromHash();
    var start = Math.max(0, Math.min(panels.length - 1, (parseInt(root.getAttribute('data-ch-start'), 10) || 1) - 1));
    select(initial > -1 ? initial : start, { smooth: false });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-soah-chapters]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme Editor: the section root is replaced on reload, so the fresh root
  // arrives unbound and the previous listeners are discarded with the old node.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
